import { useApolloClient, useMutation } from '@apollo/client';
import { Text, Tooltip } from '@radix-ui/themes';
import { Pin } from 'lucide-react';
import { use, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PIN_JOB, UNPIN_JOB } from '../../behavior/queries/jobHeader';
import LocaleContext from '../hooks/LocaleContext';
import { track } from '../../analytics/track';
import { AnalyticsEvents } from '../../analytics/events';
import { PINNED_JOBS_QUERY } from '../navbar/PinnedJobs';

const JobPin = ({ job }: { job: { id: string; title: string; pinned: boolean } }) => {
  const { t } = useTranslation();
  const [pinned, setPinned] = useState(!!job.pinned);

  useEffect(() => {
    setPinned(!!job.pinned);
  }, [job.pinned]);

  const [pinJob] = useMutation(PIN_JOB, {
    update(cache, { data: { pinJob } }) {
      cache.writeQuery({
        query: PINNED_JOBS_QUERY,
        data: {
          pinnedJobs: {
            __typename: 'User',
            id: pinJob.id,
            pinnedJobs: pinJob.pinnedJobs,
          },
        },
      });
    },
  });
  const [unpinJob] = useMutation(UNPIN_JOB, {
    update(cache, { data: { unpinJob } }) {
      cache.writeQuery({
        query: PINNED_JOBS_QUERY,
        data: {
          pinnedJobs: {
            __typename: 'User',
            id: unpinJob.id,
            pinnedJobs: unpinJob?.pinnedJobs
              ? unpinJob.pinnedJobs.filter((tempJob: { id: string }) => tempJob.id !== job.id)
              : [],
          },
        },
      });
    },
  });
  const { userSettings } = use(LocaleContext);

  const client = useApolloClient();

  const togglePin = async () => {
    track(AnalyticsEvents.JOB_PIN_TOGGLED, { pinned: !pinned });
    const userPinnedJobs = await client.readQuery<{ pinnedJobs: { pinnedJobs: { id: string }[] } }>({
      query: PINNED_JOBS_QUERY,
    });

    const pinnedJobs = userPinnedJobs?.pinnedJobs?.pinnedJobs ?? [];

    if (pinned === true) {
      unpinJob({
        variables: {
          jobId: job.id,
        },
        optimisticResponse: {
          unpinJob: {
            __typename: 'User',
            id: userSettings.id,
            pinnedJobs: pinnedJobs.filter((tempJob) => tempJob.id !== job.id),
          },
        },
      });
    } else if (pinned === false) {
      pinJob({
        variables: {
          jobId: job.id,
        },
        optimisticResponse: {
          pinJob: {
            __typename: 'User',
            id: userSettings.id,
            pinnedJobs: [
              ...pinnedJobs,
              {
                __typename: 'Job',
                id: job.id,
                title: job.title,
                pinned: true,
                location: { __typename: 'JobLocation', name: null },
              },
            ],
          },
        },
      });
    }

    setPinned((prevPinned) => !prevPinned);
  };

  return (
    <div onClick={togglePin} className="ml-1 clickable">
      <Tooltip
        side="bottom"
        content={<Text size="3">{pinned ? t('jobPin.tooltip.unpin') : t('jobPin.tooltip.pin')}</Text>}
      >
        <Pin className="job-pin-icon" fill={pinned ? '#636366' : 'none'} />
      </Tooltip>
    </div>
  );
};

export default JobPin;
